// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Ctx } from '../main.ts';
import { GROUP_META, GROUP_ORDER, type Group } from '../parties.ts';
import { fmtInt, fmtPct, fmtSigned, esc, escTip } from '../utils/format.ts';
import { term } from '../glossary.ts';

// Deterministic parliament-diagram seat positions across `rows` arcs, ordered
// left→right by angle so party groups form contiguous wedges.
function horseshoe(n: number, rows: number, rInner: number, rOuter: number): { x: number; y: number }[] {
  const radii: number[] = [];
  for (let i = 0; i < rows; i++) radii.push(rInner + ((rOuter - rInner) * i) / Math.max(1, rows - 1));
  const weight = radii.reduce((a, r) => a + r, 0);
  const counts = radii.map((r) => Math.max(1, Math.round((n * r) / weight)));
  let diff = n - counts.reduce((a, b) => a + b, 0);
  // Adjust to sum exactly to n, nudging the outer rows first.
  for (let i = rows - 1; diff !== 0 && i >= 0; i = i === 0 ? rows - 1 : i - 1) {
    counts[i] += diff > 0 ? 1 : -1;
    diff += diff > 0 ? -1 : 1;
  }
  const pts: { x: number; y: number; a: number }[] = [];
  radii.forEach((r, i) => {
    const c = counts[i];
    for (let j = 0; j < c; j++) {
      const a = c === 1 ? Math.PI / 2 : (Math.PI * j) / (c - 1);
      pts.push({ x: -r * Math.cos(a), y: -r * Math.sin(a), a });
    }
  });
  pts.sort((p, q) => p.a - q.a);
  return pts.map(({ x, y }) => ({ x, y }));
}

export function renderOverview(root: HTMLElement, ctx: Ctx): void {
  const { national: n } = ctx.data;
  const govGroup: Group = 'ALP';
  const groupsInOrder = GROUP_ORDER.filter((g) => (n.seatsByGroup[g] || 0) > 0);

  // Build the horseshoe, assign the ordered group runs left→right.
  const pts = horseshoe(150, 8, 150, 300);
  // Left→right: government, then crossbench, then opposition (conventional horseshoe).
  const seatOrder: Group[] = ['ALP', 'GRN', 'IND', 'OTH', 'COA'];
  const groupSeq: Group[] = [];
  for (const g of seatOrder) for (let i = 0; i < (n.seatsByGroup[g] || 0); i++) groupSeq.push(g);
  const dots = pts.map((p, i) => ({ ...p, g: groupSeq[i] }));

  const dotSvg = `
    <svg viewBox="-320 -320 640 340" class="horseshoe" role="img" aria-label="Composition of the House of Representatives: ${groupsInOrder.map((g) => `${GROUP_META[g].label} ${n.seatsByGroup[g]}`).join(', ')}">
      ${dots.map((d) => `<circle cx="${d.x.toFixed(1)}" cy="${d.y.toFixed(1)}" r="7.6" fill="${GROUP_META[d.g].color}" data-tip="${escTip(`${GROUP_META[d.g].label}`)}"><title>${GROUP_META[d.g].label}</title></circle>`).join('')}
      <text x="0" y="-70" text-anchor="middle" font-size="42" font-weight="800" fill="#0f1c2e" font-family="var(--font-mono)">${n.seatsByGroup[govGroup]}</text>
      <text x="0" y="-40" text-anchor="middle" font-size="15" fill="#475569">Labor seats · majority of 76</text>
    </svg>`;

  const tppW = n.tppALPPct;
  const primaryTop = ctx.data.parties.slice(0, 6);
  const maxPrimary = Math.max(...primaryTop.map((p) => p.pct));

  root.innerHTML = `
    <div class="view-head">
      <h2>Australia voted in Labor, decisively</h2>
      <p class="subtitle">The 2025 federal election returned the Albanese government with 94 of 150 seats and a ${fmtPct(n.tppALPPct)} ${term('tpp', 'two-party-preferred')} vote — a ${fmtSigned(n.tppSwingToALP)}-point swing. Explore every seat, swing and preference below.</p>
    </div>

    <div class="stat-grid" style="margin-bottom:var(--sp-xl)">
      <div class="stat" style="border-left-color:var(--alp)"><div class="k">Government</div><div class="v">Labor</div><div class="d">${n.seatsByGroup.ALP} seats · Anthony Albanese re-elected</div></div>
      <div class="stat" style="border-left-color:var(--coa)"><div class="k">Opposition</div><div class="v">${n.coalitionSeats}</div><div class="d">Coalition seats (Liberal, LNP, Nationals, CLP)</div></div>
      <div class="stat" style="border-left-color:var(--ind)"><div class="k">Crossbench</div><div class="v">${n.seatsByGroup.GRN + n.seatsByGroup.IND + n.seatsByGroup.OTH}</div><div class="d">${n.seatsByGroup.IND} independents · ${n.seatsByGroup.GRN} Green · ${n.seatsByGroup.OTH} other</div></div>
      <div class="stat" style="border-left-color:var(--navy)"><div class="k">Turnout</div><div class="v">${n.turnoutPct.toFixed(1)}%</div><div class="d">${fmtInt(n.turnout)} votes · ${n.informalPct.toFixed(1)}% informal</div></div>
    </div>

    <div class="panel">
      <div class="panel-title">The 48th Parliament — House of Representatives</div>
      <div class="panel-sub">Every dot is one of the 150 seats, coloured by the party that won it. 76 is a majority.</div>
      ${dotSvg}
      <div class="legend" style="justify-content:center;margin-top:var(--sp-md)">
        ${groupsInOrder.map((g) => `<span class="li"><span class="sw" style="background:${GROUP_META[g].color}"></span>${GROUP_META[g].label} ${n.seatsByGroup[g]}</span>`).join('')}
      </div>
    </div>

    <div class="panel">
      <div class="panel-title">Two-party-preferred vote</div>
      <div class="panel-sub">After ${term('preferences')} are distributed, the national split between Labor and the Coalition.</div>
      <div class="compbar" data-tip="${escTip(`Labor ${fmtPct(n.tppALPPct)} · Coalition ${fmtPct(n.tppLNPPct)}`)}">
        <div style="background:var(--alp);width:${tppW}%">${fmtPct(n.tppALPPct)} Labor</div>
        <div style="background:var(--coa);width:${100 - tppW}%">Coalition ${fmtPct(n.tppLNPPct)}</div>
      </div>
      <p class="note">Swing of ${fmtSigned(n.tppSwingToALP)} points to Labor since 2022 (adjusted for boundary changes).</p>
    </div>

    <div class="panel">
      <div class="panel-title">First-preference vote — the primary count</div>
      <div class="panel-sub">Before preferences: who voters put first. The two majors together fell to a record low.</div>
      <div id="primary-bars"></div>
      <p class="note">Full breakdown, including every party and each state, in the <a href="#v=firstprefs">First preferences</a> view.</p>
    </div>

    <div class="panel" style="background:linear-gradient(180deg,#fff,#f4f6f9)">
      <div class="panel-title">Where to look next</div>
      <div class="panel-sub">The election in one line, then the tools to check the detail.</div>
      <div class="insight-grid">
        <div class="insight alert"><h4>An opposition leader lost his own seat</h4><p>Peter Dutton lost Dickson to Labor's Ali France — a first at a general election. <a class="seatlink" href="#v=map&seat=Dickson">Open Dickson →</a></p></div>
        <div class="insight good"><h4>The closest seat: 26 votes</h4><p>Bradfield went to teal independent Nicolette Boele over the Liberals by the narrowest margin in the country. <a class="seatlink" href="#v=map&seat=Bradfield">Open Bradfield →</a></p></div>
        <div class="insight"><h4>See the whole board</h4><p>The <a class="seatlink" href="#v=pendulum">Mackerras pendulum</a> lines up all 150 seats by margin, and the <a class="seatlink" href="#v=preferences">preference flows</a> show who really decided them.</p></div>
      </div>
    </div>`;

  // Primary-vote bars.
  const bars = root.querySelector('#primary-bars')!;
  bars.innerHTML = primaryTop.map((p) => {
    const color = p.ab === 'ALP' ? GROUP_META.ALP.color : p.group === 'COA' ? GROUP_META.COA.color : GROUP_META[p.group].color;
    return `<div class="barrow">
      <span class="lab" title="${esc(p.name)}">${esc(p.name)}</span>
      <span class="track"><span class="fill" style="width:${(p.pct / maxPrimary) * 100}%;background:${color}" data-tip="${escTip(`${p.name}\n${fmtPct(p.pct)} of first preferences\n${fmtInt(p.votes)} votes\nSwing ${fmtSigned(p.swing)}`)}"></span></span>
      <span class="val">${fmtPct(p.pct)}</span>
    </div>`;
  }).join('');
}
